/**
 * Review screen — the Build hub the visitor lands in once a build hits
 * `ready`. Phase 2 wires the *structural shell* of the prototype's
 * `Setup.jsx` (left-rail nav + right-side content + sticky go-live bar),
 * with three sections actually rendering content (Overview, Knowledge
 * base, Test & preview) and six Phase-3-deferred sections rendering
 * inert "Coming next" placeholders so the visitor can see the shape of
 * what's coming.
 *
 * The visitor cannot edit anything in Phase 2 — KB editing, voice
 * picker, custom-instruction editor, capability toggles, integrations,
 * activation modal, and embed/share all arrive in Phase 3. The voice
 * and chat testers ship in separate parallel PRs.
 *
 * Backend contract is unchanged from PR #25: `/kb/{slug}` returns
 * `{ slug, build, profile }` and the Review screen renders off the
 * `profile.kb_groups` shape exactly as before.
 */

import { useEffect, useRef, useState } from "react";

import type {
  DailyCall,
  DailyEventObjectFatalError,
  DailyEventObjectNonFatalError,
  DailyEventObjectTrack,
} from "@daily-co/daily-js";

import { API_URL } from "./api.js";
import { ComingNext } from "./ComingNext.js";
import { GoLiveBar } from "./GoLiveBar.js";
import { HubNav, type NavItem } from "./HubNav.js";
import { Icon } from "./Icon.js";
import { KbDisplay } from "./KbDisplay.js";
import { pickHoursValue } from "./kbHours.js";
import { Overview } from "./Overview.js";
import type { Build, BusinessProfile, Vertical } from "./types.js";

/** Pretty-print a vertical enum value for the business-header. */
function verticalLabel(v: Vertical): string {
  const labels: Record<Vertical, string> = {
    trades: "Trades",
    home_mobile_services: "Home & mobile services",
    accountants_bookkeepers_brokers: "Professional services",
    beauty: "Beauty",
    pet_care: "Pet care",
    fitness_lessons: "Fitness & lessons",
    hospitality: "Hospitality",
  };
  return labels[v] ?? v;
}

interface ReviewProps {
  build: Build;
  profile: BusinessProfile;
  onProfileUpdate: (profile: BusinessProfile) => void;
}

/** Top-level hub container — owns the active-tab state. */
export function Review({ build, profile, onProfileUpdate }: ReviewProps) {
  const [active, setActive] = useState<string>("overview");

  const navItems: NavItem[] = [
    {
      id: "overview",
      icon: "layout-dashboard",
      label: "Overview",
      enabled: true,
    },
    {
      id: "knowledge",
      icon: "book-open",
      label: "Knowledge base",
      enabled: true,
    },
    {
      id: "voice",
      icon: "mic",
      label: "Voice & greeting",
      enabled: false,
    },
    {
      id: "instructions",
      icon: "list",
      label: "Custom instructions",
      enabled: false,
    },
    { sep: true },
    {
      id: "capabilities",
      icon: "wand-2",
      label: "Capabilities",
      enabled: false,
    },
    {
      id: "integrations",
      icon: "plug",
      label: "Integrations",
      enabled: false,
    },
    { sep: true },
    {
      id: "test",
      icon: "play-circle",
      label: "Test & preview",
      enabled: true,
    },
    {
      id: "share",
      icon: "share-2",
      label: "Embed & share",
      enabled: false,
    },
    {
      id: "advanced",
      icon: "sliders-horizontal",
      label: "Advanced",
      enabled: false,
    },
  ];

  return (
    <div className="fade-up">
      <div className="hub">
        <HubNav
          businessName={profile.name}
          businessType={verticalLabel(profile.vertical)}
          items={navItems}
          active={active}
          onSelect={setActive}
        />

        <div className="hub-content" data-testid="hub-content">
          {active === "overview" && (
            <Overview kbGroups={profile.kb_groups} onNavigate={setActive} />
          )}
          {active === "knowledge" && (
            <KbDisplay
              slug={profile.slug}
              profile={profile}
              onProfileUpdate={onProfileUpdate}
            />
          )}
          {active === "voice" && (
            <ComingNext
              title="Voice & greeting"
              blurb="Pick how your offsider sounds and what it says when it picks up."
              body="Voice picker, manner controls and the greeting editor land here in the next update."
              icon="mic"
            />
          )}
          {active === "instructions" && (
            <ComingNext
              title="Custom instructions"
              blurb="Plain-English lines your receptionist always follows."
              body="An ordered list of always-on instructions (add, edit, reorder, delete) arrives here in Phase 3."
              icon="list"
            />
          )}
          {active === "capabilities" && (
            <ComingNext
              title="Capabilities"
              blurb="Decide what your offsider can do beyond answering questions."
              body="Capability toggles — taking bookings, sending callbacks, deposits — turn up here in Phase 3."
              icon="wand-2"
            />
          )}
          {active === "integrations" && (
            <ComingNext
              title="Integrations"
              blurb="Connect the tools your business already runs on."
              body="OAuth-based connectors (Square, Stripe, Google Calendar, etc.) land here in Phase 3."
              icon="plug"
            />
          )}
          {active === "test" && (
            <TestPreviewSection
              slug={profile.slug}
              businessName={profile.name}
              buildId={build.id}
              profile={profile}
            />
          )}
          {active === "share" && (
            <ComingNext
              title="Embed & share"
              blurb="Drop a chat widget on your site or hand out the public link."
              body="Embed snippets and shareable URLs ship in Phase 3, once activation is wired."
              icon="share-2"
            />
          )}
          {active === "advanced" && (
            <ComingNext
              title="Advanced"
              blurb="Power-user settings: fall-backs, escalation rules, raw prompt overrides."
              body="The advanced settings panel arrives in Phase 3 once the core hub is fully wired."
              icon="sliders-horizontal"
            />
          )}
        </div>
      </div>

      <GoLiveBar onTest={() => setActive("test")} />
    </div>
  );
}

/**
 * Test & preview tab — the Voice tester now opens a real, hands-free
 * interactive call against the server (Pipecat + Nova Sonic 2 in Tokyo,
 * grounded in the visitor's `BusinessProfile`). The Chat tester ships
 * later, so it stays in the "Coming next" placeholder shape.
 */
function TestPreviewSection({
  slug,
  businessName,
  buildId,
  profile,
}: {
  slug: string;
  businessName: string;
  buildId: string;
  profile: BusinessProfile;
}) {
  return (
    <div data-testid="test-preview-tab">
      <div className="section-head">
        <h2>Take it for a spin</h2>
        <p>
          Have a chat or call with {businessName}'s offsider, right here in the
          browser.
        </p>
      </div>

      <FreeTest profile={profile} />

      <div className="tp-grid">
        <VoiceTester slug={slug} businessName={businessName} />

        <div
          className="panel try-tile"
          aria-disabled="true"
          style={{ opacity: 0.6 }}
        >
          <span className="lock-pill">
            <Icon name="info" size={11} /> Coming next
          </span>
          <div className="tt-ico" style={{ background: "var(--bg-2)" }}>
            <Icon name="message-square" size={26} color="var(--ink-3)" />
          </div>
          <h3>Chat tester</h3>
          <p>
            Type a few questions and see how the offsider answers — handy for
            checking pricing, hours and the bits flagged "needs you" above.
          </p>
          <button className="btn btn-secondary btn-block" disabled>
            Coming soon
          </button>
        </div>
      </div>

      <div
        className="t-micro"
        style={{
          marginTop: 14,
          color: "var(--ink-3)",
          textAlign: "center",
        }}
      >
        Build id:{" "}
        <code style={{ fontFamily: "var(--font-mono)" }}>
          {buildId.slice(0, 8)}
        </code>
      </div>
    </div>
  );
}

/**
 * Free test surface. Closes the BDD scenario in
 * `data/bdd/features/build-link-to-kb.feature`: the operator asks
 * "are you open now" and the surface answers from the typed business
 * profile (the Hours KB group) without a live voice call or backend
 * round-trip. Real LLM grounding lands in Phase 3.
 */
function FreeTest({ profile }: { profile: BusinessProfile }) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);

  function answerFrom(q: string): string {
    const lower = q.toLowerCase();
    // Same hours selector the Overview glance uses, so the test answer
    // echoes the exact value the glance shows.
    const hoursValue = pickHoursValue(profile.kb_groups) ?? "";
    if (/open|hours|right now|today/.test(lower)) {
      if (hoursValue) {
        return `Our hours: ${hoursValue}.`;
      }
      return "I don't have hours on file yet, give us a couple of minutes.";
    }
    return "I can answer hours, services, and location questions from the typed profile.";
  }

  function ask() {
    const q = question.trim();
    if (!q) return;
    setAnswer(answerFrom(q));
  }

  return (
    <div
      className="panel"
      data-testid="free-test-surface"
      style={{ marginBottom: 18, padding: 18 }}
    >
      <h3 style={{ marginTop: 0, marginBottom: 8 }}>Free test</h3>
      <p style={{ color: "var(--ink-2)", marginTop: 0 }}>
        Ask a question your callers would ask. The offsider answers from the
        typed profile above.
      </p>
      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 12,
          flexWrap: "wrap",
        }}
      >
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") ask();
          }}
          placeholder="are you open now"
          aria-label="Test question"
          data-testid="free-test-question"
          style={{
            flex: 1,
            minWidth: 220,
            padding: "10px 12px",
            borderRadius: 8,
            border: "1px solid var(--rule)",
            fontSize: 14,
          }}
        />
        <button
          type="button"
          className="btn btn-primary"
          onClick={ask}
          data-testid="free-test-ask"
        >
          Ask
        </button>
      </div>
      {answer && (
        <div
          data-testid="free-test-answer"
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 8,
            background: "var(--bg-1)",
            fontSize: 14,
            color: "var(--ink-1)",
          }}
        >
          {answer}
        </div>
      )}
    </div>
  );
}

/**
 * Discriminated UI state for the Voice tester.
 *
 * Transitions are linear during a happy-path call:
 *   idle → requesting-mic → connecting → live → ended
 * Any step may bail to `error`; the user can then re-`start` from there.
 */
type CallState =
  | { kind: "idle" }
  | { kind: "requesting-mic" }
  | { kind: "connecting" }
  | { kind: "live"; startedAt: number }
  | { kind: "ended" }
  | { kind: "error"; message: string };

/**
 * Live, hands-free test call against the server.
 *
 * Daily.co media plane: the browser POSTs `/voice/{slug}/room` which
 * mints a Daily room + two meeting tokens server-side (caller's token
 * returned to us, bot's kept server-side and used by the Pipecat
 * pipeline to join the same room as the bot). We hand the room URL +
 * caller token to `@daily-co/daily-js` and Daily's call object handles
 * the WebRTC + ICE + jitter buffer + opus codec setup. Audio flows
 * browser ↔ Daily SFU ↔ bot — the ALB never sees the media leg.
 *
 * Daily SDK is dynamically imported on first call so it stays out of
 * the main SPA bundle (~200KB) until someone actually clicks "Start
 * test call".
 *
 * Mic + call object are torn down on hang-up and on component unmount
 * so an unmounted tester can't keep the mic light on.
 */
function VoiceTester({
  slug,
  businessName,
}: {
  slug: string;
  businessName: string;
}) {
  const [state, setState] = useState<CallState>({ kind: "idle" });
  const [speaking, setSpeaking] = useState(false);
  const callRef = useRef<DailyCall | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const localAnalyserRef = useRef<AnalyserNode | null>(null);
  const remoteAnalyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);

  /**
   * Tear the call down without changing component state.
   *
   * Detaches Daily event handlers BEFORE leaving so the synchronous
   * `left-meeting` burst can't stomp whatever final state we want to
   * land in (the user-initiated hangup race that previously turned
   * `ended` into `error`).
   *
   * Idempotent — safe to call from hangup, error handler, and unmount.
   */
  function teardown(): void {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const call = callRef.current;
    if (call) {
      callRef.current = null;
      call.off("joined-meeting", onJoined);
      call.off("track-started", onTrackStarted);
      call.off("left-meeting", onLeft);
      call.off("error", onFatalError);
      call.off("nonfatal-error", onNonFatalError);
      // Leave + destroy are async and may reject if the call is
      // already torn down; swallow because we don't care once we're
      // tearing down ourselves.
      void call.leave().catch(() => {});
      void call.destroy().catch(() => {});
    }
    if (audioCtxRef.current) {
      void audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    localAnalyserRef.current = null;
    remoteAnalyserRef.current = null;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.srcObject = null;
    }
    setSpeaking(false);
  }

  function hangup(): void {
    teardown();
    setState({ kind: "ended" });
  }

  useEffect(() => () => teardown(), []);

  // Event handlers extracted as named refs so `teardown` can `.off`
  // them. Daily's `.off` is an exact-handler match — anonymous
  // arrow-fn handlers would leak event listeners across hang-up cycles.
  function onJoined(): void {
    setState({ kind: "live", startedAt: Date.now() });
    const call = callRef.current;
    const ctx = audioCtxRef.current;
    if (!call || !ctx) return;
    const local = call.participants().local;
    const localTrack = local?.tracks?.audio?.persistentTrack;
    if (localTrack) {
      const stream = new MediaStream([localTrack]);
      localAnalyserRef.current = attachAnalyser(stream, ctx);
    }
    startMeter(localAnalyserRef, remoteAnalyserRef, rafRef, setSpeaking);
  }

  function onTrackStarted(ev: DailyEventObjectTrack): void {
    if (ev.track.kind !== "audio" || ev.participant?.local) return;
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    // Remote (bot) audio just started — attach to the playback
    // <audio> element AND build an analyser for the speaking meter.
    const stream = new MediaStream([ev.track]);
    if (audioRef.current) {
      audioRef.current.srcObject = stream;
      void audioRef.current.play();
    }
    remoteAnalyserRef.current = attachAnalyser(stream, ctx);
  }

  function onLeft(): void {
    setState((prev) => (prev.kind === "ended" ? prev : { kind: "ended" }));
    teardown();
  }

  function onFatalError(ev: DailyEventObjectFatalError): void {
    setState((prev) =>
      prev.kind === "ended"
        ? prev
        : {
            kind: "error",
            message: ev.errorMsg || "Daily error",
          },
    );
    teardown();
  }

  function onNonFatalError(ev: DailyEventObjectNonFatalError): void {
    // Logged-but-not-fatal. Examples: track-subscription failures
    // Daily auto-retries, transient ICE renegotiations. We surface a
    // banner only if the call drops outright (handled by onLeft +
    // onFatalError).
    console.warn("daily nonfatal:", ev.type, ev.errorMsg);
  }

  async function start(): Promise<void> {
    setState({ kind: "requesting-mic" });
    try {
      const res = await fetch(
        `${API_URL}/voice/${encodeURIComponent(slug)}/room`,
        { method: "POST" },
      );
      if (!res.ok) {
        const detail = await res.text();
        throw new Error(`server ${res.status}: ${detail || res.statusText}`);
      }
      const { room_url, user_token } = (await res.json()) as {
        room_url: string;
        user_token: string;
      };

      setState({ kind: "connecting" });

      // Dynamic import keeps Daily SDK out of the main bundle. First
      // click pays the ~200KB download, subsequent calls reuse it.
      const Daily = (await import("@daily-co/daily-js")).default;
      const call = Daily.createCallObject({
        audioSource: true,
        videoSource: false,
      });
      callRef.current = call;

      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;

      call.on("joined-meeting", onJoined);
      call.on("track-started", onTrackStarted);
      call.on("left-meeting", onLeft);
      call.on("error", onFatalError);
      call.on("nonfatal-error", onNonFatalError);

      await call.join({ url: room_url, token: user_token });
    } catch (err) {
      teardown();
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : "call failed",
      });
    }
  }

  const isLive = state.kind === "live";
  const isConnecting =
    state.kind === "requesting-mic" || state.kind === "connecting";

  const button = (() => {
    if (state.kind === "idle" || state.kind === "ended") {
      return (
        <button
          type="button"
          className="btn btn-primary btn-block"
          onClick={() => void start()}
          data-testid="voice-tester-start"
        >
          {state.kind === "ended" ? "Call again" : "Start test call"}
        </button>
      );
    }
    if (state.kind === "error") {
      return (
        <button
          type="button"
          className="btn btn-primary btn-block"
          onClick={() => void start()}
          data-testid="voice-tester-retry"
        >
          Try again
        </button>
      );
    }
    return (
      <button
        type="button"
        className="btn btn-secondary btn-block"
        onClick={hangup}
        data-testid="voice-tester-hangup"
      >
        End call
      </button>
    );
  })();

  const status = (() => {
    switch (state.kind) {
      case "idle":
        return null;
      case "requesting-mic":
        return "Asking for mic access…";
      case "connecting":
        return "Connecting…";
      case "live":
        return speaking ? "On the call" : "Listening…";
      case "ended":
        return "Call ended.";
      case "error":
        return state.message;
    }
  })();

  return (
    <div
      className="panel try-tile"
      data-testid="voice-tester"
      data-state={state.kind}
    >
      {!isLive && !isConnecting && (
        <div className="tt-ico">
          <Icon name="mic" size={26} color="var(--accent)" />
        </div>
      )}
      {isConnecting && (
        <div className="try-stage" style={{ flexDirection: "column", gap: 14 }}>
          <span
            className="dial"
            style={{
              display: "inline-block",
              width: 30,
              height: 30,
              borderRadius: "50%",
              border: "3px solid var(--accent-soft)",
              borderTopColor: "var(--accent)",
            }}
          />
        </div>
      )}
      {isLive && (
        <div className="try-stage">
          <div
            className={`wave${speaking ? " speaking" : ""}`}
            data-testid="voice-tester-wave"
          >
            {Array.from({ length: 9 }).map((_, i) => (
              <span key={i} />
            ))}
          </div>
        </div>
      )}
      <h3>Voice tester</h3>
      <p>
        Talk to {businessName}'s offsider in your browser, conditioned on the
        knowledge base above.
      </p>
      {status && (
        <p
          className="t-micro"
          style={{ marginTop: 4, color: "var(--ink-3)" }}
          data-testid="voice-tester-status"
        >
          {status}
        </p>
      )}
      {button}
      <audio ref={audioRef} autoPlay playsInline />
    </div>
  );
}

/**
 * Build an `AnalyserNode` against a `MediaStream`'s audio track.
 *
 * `fftSize: 256` is the minimum that still gives a meaningful RMS read
 * per animation frame — plenty for a binary "is anyone talking" signal.
 */
function attachAnalyser(stream: MediaStream, ctx: AudioContext): AnalyserNode {
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 256;
  source.connect(analyser);
  return analyser;
}

/**
 * RAF loop that sets `speaking=true` while either side has measurable
 * recent audio energy. We OR the two sides because the CTA's wave
 * animation in the marketing surface is a single channel — caller and
 * agent both light it up — and we want the test tile to feel the same.
 */
function startMeter(
  localAnalyser: React.MutableRefObject<AnalyserNode | null>,
  remoteAnalyser: React.MutableRefObject<AnalyserNode | null>,
  rafRef: React.MutableRefObject<number | null>,
  setSpeaking: (v: boolean) => void,
): void {
  const buf = new Uint8Array(256);
  const THRESHOLD = 0.05;
  const tick = (): void => {
    let localRms = 0;
    let remoteRms = 0;
    if (localAnalyser.current) {
      localAnalyser.current.getByteTimeDomainData(buf);
      localRms = rms(buf);
    }
    if (remoteAnalyser.current) {
      remoteAnalyser.current.getByteTimeDomainData(buf);
      remoteRms = rms(buf);
    }
    setSpeaking(localRms > THRESHOLD || remoteRms > THRESHOLD);
    rafRef.current = requestAnimationFrame(tick);
  };
  rafRef.current = requestAnimationFrame(tick);
}

function rms(buf: Uint8Array): number {
  let sum = 0;
  for (const v of buf) {
    const x = (v - 128) / 128;
    sum += x * x;
  }
  return Math.sqrt(sum / buf.length);
}
